import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { TutorTask } from './tutor-tasks/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useProfileStore } from '@entities/viewer'
import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { TASK_EMPTY_CELL, formatTaskDate, isTaskOverdue } from './tutor-tasks/tutor-tasks'
import { getDisciplineName } from './discipline-general-info'
import { useDisciplineTasksStore } from './tasks/store'
import { TutorTaskModal } from './tutor-tasks/enums'

interface DisciplineTaskRow {
  task: TutorTask
  disciplineName: string
  disciplineUrl: string | null
  tutorName: string
  tutorUrl: string | null
  typeLabel: string
  dueDate: string
  isOverdue: boolean
}

interface TableDisciplineTasksReturn {
  rows: ComputedRef<DisciplineTaskRow[]>
  isLoading: ComputedRef<boolean>
  hasTasks: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  openTask: (task: TutorTask) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableDisciplineTasks = (): TableDisciplineTasksReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useDisciplineTasksStore()
  const listsStore = useListsStore()
  const profileStore = useProfileStore()
  const dayjs = useDayjs()

  const isLoading = computed<boolean>(() => store.isLoading)
  const hasTasks = computed<boolean>(() => store.hasTasks)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const rows = computed<DisciplineTaskRow[]>(() => {
    const lists = listsStore.getLists
    // Due dates are the admin's wall clock, so "now" must be too, not the browser's.
    const now = profileStore.getCurrentTime ?? dayjs().format(DateFormat.Default)

    return store.getTasks.map(task => ({
      task,
      disciplineName: task.discipline ? getDisciplineName(task.discipline.code, lists?.discipline.code) : TASK_EMPTY_CELL,
      disciplineUrl: task.discipline ? resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: task.discipline.id }) : null,
      tutorName: task.tutor?.full_name ?? '',
      tutorUrl: task.tutor ? useAdminRoutes('admin.tutor.show', { tutor_id: task.tutor.id }) : null,
      typeLabel: lists?.tutor_task.type[task.type] ?? '',
      dueDate: formatTaskDate(task.due_date),
      isOverdue: isTaskOverdue(task, now),
    }))
  })

  const openTask = (task: TutorTask): void => {
    store.openModal(TutorTaskModal.Edit, task)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    rows,
    isLoading,
    hasTasks,
    meta,
    perPageOptions,
    openTask,
    setChangePage,
    setChangePerPage,
  }
}
